//product-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { cropsApi } from "../services/api/crops";
import { Crop } from "../models/crop";
import { Box, useTheme } from "@mui/material";
import LoadingBackdrop from "../components/loading-backdrop";
import { Product } from "../models/product";
import { productsApi, ProductTypes } from "../services/api/products";
import ProductForm, { ProductFormValues } from "../components/product-form";
import Page from "../components/page";
import { Size } from "../models/size";
import { sizesApi } from "../services/api/sizes";
import { formatCurrency, toCurrency } from "./currency";
import { UserContext, ErrorContext } from "../components/app-routes";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StickyActionBar from "../components/sticky-action-bar";
import { Pkg } from "../models/pkg";
import { pkgsApi } from "../services/api/pkgs";
import { getCustomLabel } from "../utils/custom-labels";

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const [busy, setBusy] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [crops, setCrops] = useState<Crop[] | null>(null);
  const [productSizes, setProductSizes] = useState<Size[] | null>(null);
  const history = useHistory();
  const [saved, setSaved] = useState(false);
  const { grower } = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const theme = useTheme();
  const isMounted = useRef(true);
  const [pkgs, setPkgs] = useState<Pkg[] | null>(null);

  if (!grower) {
    throw new Error("Invalid UserContext");
  }

  const isPro = grower.Plan === "pro";

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function getProduct() {
      setBusy(true); // ← no guard

      try {
        const results = await Promise.all([
          productsApi.getSingle(parseInt(id)),
          cropsApi.getAll(),
          sizesApi.getAll(),
          isPro ? pkgsApi.getAll() : Promise.resolve([]),
        ]);

        if (isMounted.current) {
          setProduct(results[0]);
          setCrops(results[1]);
          setProductSizes(results[2]);
          setPkgs(results[3]);
        }
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }

    getProduct();
  }, [id]);

  const handleSubmit = async (values: ProductFormValues) => {
    if (!product) {
      throw new Error("Invalid state");
    }
    if (
      values.productCrops.length > 1 &&
      values.productSizes.some((s) => s.type === ProductTypes.Tray)
    ) {
      return handleError(new Error("Blended products cannot be sold as trays"));
    }

    setBusy(true);

    try {
      await productsApi.update({
        id: product.id,
        name: values.name,
        productCrops: values.productCrops.map((c) => ({
          id: c.id,
          crop: null as unknown as Crop,
          cropId: parseInt(c.cropId),
          blend: parseFloat(c.blend || "100"),
        })),
        productSizes: values.productSizes.map((pS) => {
          const base = {
            type: pS.type as ProductTypes,
            weight: pS.weight ? parseFloat(pS.weight) : undefined,
            sizeId: pS.sizeId ? parseInt(pS.sizeId) : undefined,
            size: null as unknown as Size,
            pkgId: pS.pkgId ? parseInt(pS.pkgId) : undefined,
            price: toCurrency(pS.price, grower.CountryISO).intValue,
          };

          // 🔑 only send id if it is a real number
          return typeof pS.id === "number" ? { id: pS.id, ...base } : base;
        }),

        notes: values.notes,
        archivedAt:
          !!product.archivedAt === values.archived
            ? product.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
      });

      if (isMounted.current) {
        setSaved(true);
        mixpanel.track("Update Product");
        goBackOrTo(history, "/home/products");
      }
    } catch (error) {
      if (isMounted.current) handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  return (
    <Page title={getCustomLabel(grower ?? null, "products")}>
      {product && crops && productSizes && pkgs && (
        <>
          <div>
            <Button
              sx={{ mb: 2 }}
              variant="contained"
              color="primary"
              component={Link}
              to={{
                pathname: "/home/products/add",
                state: {
                  product: {
                    ...product,
                    name: `${product.name} (COPY)`,
                  },
                },
              }}
              disabled={busy}
              startIcon={<ContentCopyIcon />}
              onClick={() => mixpanel.track("Duplicate Product")}
            >
              Duplicate
            </Button>
          </div>

          <ProductForm
            initialValues={{
              name: product.name,
              productCrops: product.productCrops.map((c) => ({
                id: c.id,
                cropId: c.crop.id.toString(),
                blend: c.blend.toString(),
              })),
              productSizes: product.productSizes.map((pS) => ({
                id: pS.id,
                type: pS.type,
                price: formatCurrency(pS.price, grower.CountryISO, {
                  symbol: "",
                  separator: "",
                }),
                weight: pS.weight ? pS.weight?.toString() : undefined,
                sizeId: pS.size?.id.toString(),
                pkgId: pS.pkgId?.toString() || "",
              })),
              notes: product.notes,
              archived: product.archivedAt !== null,
            }}
            onSubmit={handleSubmit}
            crops={crops}
            sizes={productSizes}
            pkgs={pkgs}
            saved={saved}
          >
            <StickyActionBar position="bottom">
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => goBackOrTo(history, "/home/products")}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={busy}
              >
                Save
              </Button>
            </StickyActionBar>
          </ProductForm>
        </>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
